import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { Reviewer, ReviewerType, TransparentPeerReviewType } from '@/types'
import { defaultReviewer } from '../store/functions'
import { trackEvent } from '../services/gtm'
import validateReviewEditorialView from '../services/validation/reviewEditorial'
import limits from '../constants/limits'

/**
 * Composable for managing reviewer functionality.
 * Handles recommended and non-preferred reviewers, TPR (Transparent Peer Review) options,
 * and integrates with EditReviewer and ReviewerListItem components.
 * @returns Object with reactive properties and methods for reviewer management
 */
export function useReviewerManagement() {
  const store = useStore()

  // State
  const isEditing = ref(false)
  const reviewerToEdit = ref<Reviewer>(defaultReviewer(ReviewerType.Recommend))
  const errorMessage = ref<string | undefined>(undefined)

  // Computed properties
  const recommendedReviewers = computed({
    get: () => store.state.activeSubmission.reviewers.filter((r: Reviewer) => r.type === ReviewerType.Recommend),
    set: (val: Reviewer[]) => {
      updateStore(val, ReviewerType.Recommend)
    },
  })

  const opposedReviewers = computed({
    get: () => store.state.activeSubmission.reviewers.filter((r: Reviewer) => r.type === ReviewerType.Oppose),
    set: (val: Reviewer[]) => {
      updateStore(val, ReviewerType.Oppose)
    },
  })

  const allReviewers = computed(() => store.state.activeSubmission.reviewers)

  const hasReachedMaxRecommendedReviewers = computed(
    () => recommendedReviewers.value.length >= limits.reviewers.counts.maxRecommended,
  )

  const hasMinimumRecommendedReviewers = computed(
    () => recommendedReviewers.value.length >= limits.reviewers.counts.minRecommended,
  )

  /**
   * Utility: Formats reviewer fields by trimming whitespace
   * @param reviewer - The reviewer to format
   */
  const formatReviewerFields = (reviewer: Reviewer): void => {
    reviewer.firstName = reviewer.firstName.trim()
    reviewer.lastName = reviewer.lastName.trim()
    reviewer.email = reviewer.email.trim()
    if (reviewer.reason) reviewer.reason = reviewer.reason.trim()
  }

  /**
   * Utility: Gets dialog header text based on reviewer type
   * @param type - Type of reviewer
   */
  const getReviewerDialogHeader = (type: ReviewerType): string => {
    return type === ReviewerType.Recommend
      ? 'Recommended reviewer information'
      : 'Non-preferred reviewer information'
  }

  /**
   * Utility: Gets reason help text based on reviewer type
   * @param type - Type of reviewer
   */
  const getReasonHelpText = (type: ReviewerType): string => {
    const maxChars = limits.reviewers.characters.reason
    return type === ReviewerType.Recommend
      ? `Please provide a short reason for recommending this reviewer. Maximum ${maxChars} characters.`
      : `Please provide a short reason why this reviewer is not suitable. Maximum ${maxChars} characters.`
  }

  /**
   * Utility: Checks if a reviewer can be deleted (exists in store)
   * @param reviewerId - ID of the reviewer to check
   */
  const canDeleteReviewer = (reviewerId: string): boolean => {
    return store.state.activeSubmission.reviewers.some((r: Reviewer) => r.id === reviewerId)
  }

  /**
   * Utility: Gets reviewer list position for a specific reviewer
   * @param reviewerId - ID of the reviewer
   * @param type - Type of reviewer
   * @returns Position (1-indexed) of the reviewer in the list, or list.length + 1 for new reviewers
   */
  const getReviewerPosition = (reviewerId: string, type: ReviewerType): number => {
    const list = type === ReviewerType.Recommend ? recommendedReviewers.value : opposedReviewers.value
    const position = list.findIndex((r: Reviewer) => r.id === reviewerId) + 1
    // If reviewer not found (position = 0), return next position for new reviewer
    return position > 0 ? position : list.length + 1
  }

  /**
   * Utility: Generates consistent IDs for reviewer form fields and elements
   * @param type - Type of reviewer
   * @param position - Position in the list
   * @param fieldName - Name of the field (e.g., 'FirstName', 'Email')
   */
  const generateReviewerId = (type: ReviewerType, position: number, fieldName: string): string => {
    const prefix = type === ReviewerType.Recommend ? 'recommendedReviewer' : 'nonPreferredReviewer'
    return `${prefix}${fieldName}-${position}`
  }

  /**
   * Utility: Gets full name of a reviewer
   * @param reviewer - The reviewer object
   */
  const getReviewerFullName = (reviewer: Reviewer): string => {
    return `${reviewer.firstName} ${reviewer.lastName}`.trim()
  }

  /**
   * Utility: Gets display text for TPR choice
   * @param tprType - The TPR type
   */
  const getTPRChoiceText = (tprType: TransparentPeerReviewType): string => {
    if (tprType === TransparentPeerReviewType.Agree) {
      return 'I choose transparent peer review'
    }
    else if (tprType === TransparentPeerReviewType.Decline) {
      return 'I do not choose transparent peer review'
    }
    return ''
  }

  /**
   * Utility: Checks if an email is already used by another reviewer
   * @param email - Email to check
   * @param reviewerId - ID of the current reviewer (to exclude from check when editing)
   * @returns true if the email is already used by a different reviewer
   */
  const hasDuplicateEmail = (email: string, reviewerId: string): boolean => {
    return allReviewers.value.some((r: Reviewer) =>
      r.email.toLowerCase() === email.toLowerCase() && r.id !== reviewerId,
    )
  }

  /**
   * Updates the store with reordered reviewer lists (for drag-and-drop functionality)
   * @param newList - Updated list of reviewers of a specific type
   * @param type - Type of reviewer (Recommend or Oppose)
   */
  function updateStore(newList: Reviewer[], type: ReviewerType): void {
    const otherType = type === ReviewerType.Recommend ? ReviewerType.Oppose : ReviewerType.Recommend
    const otherList = store.state.activeSubmission.reviewers.filter((r: Reviewer) => r.type === otherType)
    store.commit('setReviewers', [...newList, ...otherList])
  }

  /**
   * Helper to track reviewer-related events
   */
  const trackReviewerEvent = (action: string, event: string, reviewerType?: ReviewerType) => {
    trackEvent({
      category: 'User Interaction',
      action,
      event,
      location: 'ReviewEditorial',
      ...(reviewerType && { reviewerType }),
      submissionId: store.state.activeSubmission.submissionId,
    })
  }

  /**
   * Tracks TPR selection in GTM
   * @param tprType - The TPR type selected
   */
  const trackTPRSelection = (tprType: TransparentPeerReviewType) => {
    trackEvent({
      category: 'User Interaction',
      action: 'select',
      event: 'selectTPRType',
      location: 'ReviewEditorial',
      tprType,
      submissionId: store.state.activeSubmission.submissionId,
    })
  }

  const transparentPeerReview = computed({
    get: () => store.state.activeSubmission.transparentPeerReview,
    set: async (value: TransparentPeerReviewType) => {
      await store.dispatch('setTPRType', value)
      trackTPRSelection(value)
    },
  })

  /**
   * Opens the reviewer dialog to add or edit a reviewer
   * @param reviewer - The reviewer to edit (or a new default reviewer)
   */
  const openReviewerDialog = (reviewer: Reviewer) => {
    errorMessage.value = undefined
    reviewerToEdit.value = reviewer
    isEditing.value = true

    const isExistingReviewer = store.state.activeSubmission.reviewers.some((r: Reviewer) => r.id === reviewer.id)
    if (isExistingReviewer) {
      trackReviewerEvent('edit', 'editReviewer', reviewer.type)
    }
  }

  /**
   * Closes the reviewer dialog
   */
  const closeReviewerDialog = () => {
    isEditing.value = false
    reviewerToEdit.value = defaultReviewer(ReviewerType.Recommend)
  }

  /**
   * Opens dialog to add a new reviewer
   * @param type - Type of reviewer to add (Recommend or Oppose)
   */
  const addReviewer = (type: ReviewerType) => {
    const eventName = type === ReviewerType.Recommend ? 'addRecommendedReviewer' : 'addNonPreferredReviewer'
    trackReviewerEvent('click', eventName)
    openReviewerDialog(defaultReviewer(type))
  }

  /**
   * Validates required and optional reviewer fields
   * @param reviewer - The reviewer to validate
   * @returns boolean indicating if the reviewer is valid
   */
  const validateReviewer = (reviewer: Reviewer): boolean => {
    // Helper to validate a single field
    const validateField = (value: string | undefined, fieldName: string, maxLength: number, required = true): boolean => {
      const trimmedValue = value?.trim() || ''

      if (required && !trimmedValue) {
        errorMessage.value = `${fieldName} is required`
        return false
      }

      if (trimmedValue && trimmedValue.length > maxLength) {
        errorMessage.value = `${fieldName} exceeds ${maxLength} character limit`
        return false
      }

      return true
    }

    const validations = [
      validateField(reviewer.firstName, 'First name', limits.reviewers.characters.firstName),
      validateField(reviewer.lastName, 'Last name', limits.reviewers.characters.lastName),
      validateField(reviewer.email, 'Email', limits.reviewers.characters.email),
      validateField(reviewer.reason, 'Reason', limits.reviewers.characters.reason, false),
    ]

    const isValid = validations.every(result => result)
    if (isValid) {
      errorMessage.value = undefined
    }

    return isValid
  }

  /**
   * Helper to track error events
   */
  const trackErrorEvent = (action: string, event: string, errorDetails: string, reviewerType?: ReviewerType, errorType = 'Error') => {
    trackEvent({
      category: 'Error',
      action,
      event,
      location: 'ReviewEditorial',
      errorType,
      errorDetails,
      ...(reviewerType && { reviewerType }),
      submissionId: store.state.activeSubmission.submissionId,
    })
  }

  /**
   * Saves a reviewer (add or update)
   * @param reviewer - The reviewer to save
   */
  const handleSaveReviewer = async (reviewer: Reviewer) => {
    formatReviewerFields(reviewer)

    if (!validateReviewer(reviewer)) {
      trackErrorEvent('validation', 'saveReviewerValidationError', errorMessage.value || 'Unknown validation error', reviewer.type, 'ValidationError')
      return
    }

    try {
      const isNewReviewer = !store.state.activeSubmission.reviewers.some((r: Reviewer) => r.id === reviewer.id)
      await store.dispatch('upsertReviewer', reviewer)

      const action = isNewReviewer ? 'add' : 'update'
      const event = isNewReviewer ? 'addReviewer' : 'updateReviewer'
      trackReviewerEvent(action, event, reviewer.type)

      closeReviewerDialog()
    }
    catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred'
      errorMessage.value = `Failed to save reviewer: ${errorMsg}`
      trackErrorEvent('save', 'saveReviewerError', errorMsg, reviewer.type, 'SaveError')
    }
  }

  /**
   * Deletes a reviewer
   * @param reviewer - The reviewer to delete
   */
  const handleDeleteReviewer = async (reviewer: Reviewer) => {
    try {
      await store.dispatch('removeReviewer', reviewer.id)
      trackReviewerEvent('delete', 'deleteReviewer', reviewer.type)
      closeReviewerDialog()
    }
    catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred'
      errorMessage.value = `Failed to remove reviewer: ${errorMsg}`
      trackErrorEvent('delete', 'deleteReviewerError', errorMsg, reviewer.type, 'DeleteError')
    }
  }

  /**
   * Sets the Transparent Peer Review (TPR) type
   * @param tprType - The TPR type to set
   */
  const setTPRType = async (tprType: TransparentPeerReviewType) => {
    try {
      await store.dispatch('setTPRType', tprType)
      trackTPRSelection(tprType)
    }
    catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred'
      errorMessage.value = `Failed to set TPR type: ${errorMsg}`
      trackEvent({
        category: 'Error',
        action: 'set',
        event: 'setTPRTypeError',
        location: 'ReviewEditorial',
        errorType: 'SetTPRError',
        errorDetails: errorMsg,
        tprType,
        submissionId: store.state.activeSubmission.submissionId,
      })
    }
  }

  /**
   * Validates the review editorial view
   */
  const validateReviewers = async () => {
    try {
      await validateReviewEditorialView(store)
    }
    catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred'
      errorMessage.value = `Validation error: ${errorMsg}`
      trackEvent({
        category: 'Error',
        action: 'validation',
        event: 'validateReviewersError',
        location: 'ReviewEditorial',
        errorType: 'ValidationError',
        errorDetails: errorMsg,
        submissionId: store.state.activeSubmission.submissionId,
      })
    }
  }

  return {
    // State
    isEditing,
    reviewerToEdit,
    errorMessage,

    // Computed - Reviewer Lists
    recommendedReviewers,
    opposedReviewers,
    allReviewers,

    // Computed - Validation States
    hasReachedMaxRecommendedReviewers,
    hasMinimumRecommendedReviewers,

    // Computed - Transparent Peer Review
    transparentPeerReview,

    // Dialog Methods
    openReviewerDialog,
    closeReviewerDialog,

    // Reviewer CRUD Methods
    addReviewer,
    handleSaveReviewer,
    handleDeleteReviewer,

    // Validation
    validateReviewer,
    validateReviewers,

    // Other Methods
    setTPRType,
    updateStore,

    // Utility Functions
    formatReviewerFields,
    getReviewerDialogHeader,
    getReasonHelpText,
    canDeleteReviewer,
    getReviewerPosition,
    generateReviewerId,
    getReviewerFullName,
    getTPRChoiceText,
    hasDuplicateEmail,
  }
}
